import React from 'react';

import {
  DateField,
  If,
  SimpleBlock,
  TextField,
  useRowVisibilityHelper,
  useShowContextRequired
} from 'ui-builder';
import { PlanTask, RecipientType, TaskType } from 'nekst-api';
import { EmailField, EventField, SmsField } from 'features/tasks-list-feature';
import styles from '../../../shared/widgets/TaskRowLayout.module.scss';

import AssignmentsField from './AssignmentsField';
import DueDateField from './DueDateField';
import IsKeyDateField from './IsKeyDateField';
import LinkField from '../../../shared/uibuilder/show/field/LinkField';
import TaskRowLayout from '../../../shared/widgets/TaskRowLayout';
import IsPrivateField from './IsPrivateField';
import IsRoadmapField from './IsRoadmapField';
import IsAutoSentField from './IsAutoSentField';
import useAuthorizationChecker from '../../../shared/authorization/authorizationChecker';
import useDeletePlanTaskPopupManager from './DeletePlanTaskPopup';
import usePlanTasksNavigator from '../planTasksNavigator';
import { PlanPermission } from 'authorization-scope';
import TasksGroupField from './TasksGroupField';
import IsAiTaskField from './IsAiTaskField';
import { IconButton, IconType, SelectForMassUpdateCheckbox } from 'features/nekst-widgets';
import IsBusinessDay from './IsBusinessDay';

function OpenEditFormField() {
  const context = useShowContextRequired<PlanTask>();

  const planTasksNavigator = usePlanTasksNavigator();

  const openEditForm = () => {
    planTasksNavigator.openEditTaskForm(context.data);
  };

  return (
    <LinkField
      uri={undefined}
      source="_renderedName"
      onClick={openEditForm}
    />
  );
}

function DeletePlanTaskButton() {
  const { isGranted } = useAuthorizationChecker();

  const context = useShowContextRequired<PlanTask>();

  const deletePopupManager = useDeletePlanTaskPopupManager();

  if (isGranted(PlanPermission.UPDATE)) {
    const onClick = () => deletePopupManager.openPopup(context.data.id, context.data.name);
    return (
      <IconButton
        type={IconType.TRASH_CAN}
        className={styles.deleteButton}
        onClick={onClick}
        title="Delete Task"
      />
    );
  } else {
    return null;
  }
}

function SourcePlanLabel() {
  const context = useShowContextRequired<PlanTask>();

  if (context.data._sourcePlan) {
    return (
      <span>{context.data._sourcePlan.name}</span>
    );
  } else {
    return null;
  }
}

export default function PlanTaskRow(
  props: {
    shortView?: boolean;
    showSourcePlanLabel?: boolean;
  }
) {
  const visibilityHelper = useRowVisibilityHelper();

  return (
    <TaskRowLayout
      isDisabled={visibilityHelper.isDisabled()}
      checkbox={
        <SelectForMassUpdateCheckbox />
      }
      title={(
        <>
          {props.shortView ? (
            <IsKeyDateField source="isKeyDate" />
          ) : null}
          <EmailField />
          <SmsField />
          <EventField />
          {props.shortView ? <TextField source="name" /> : <OpenEditFormField />}
        </>
      )}
      titleAnnotation={props.showSourcePlanLabel ? <SourcePlanLabel /> : undefined}
      leftLine2={!props.shortView ? (
        <>
          <AssignmentsField source="transactionParties" />
          <If conditionFunc={(data) => data.type === TaskType.EMAIL}>
            <AssignmentsField
              source="emailRecipients"
              label="TO"
              filterFunc={(data) => data.type === RecipientType.RECIPIENT}
            />
            <AssignmentsField
              source="emailRecipients"
              label="CC"
              filterFunc={(data) => data.type === RecipientType.CC}
            />
            <AssignmentsField
              source="emailRecipients"
              label="BCC"
              filterFunc={(data) => data.type === RecipientType.BCC}
            />
          </If>
          <If conditionFunc={(data) => data.type === TaskType.SMS}>
            <AssignmentsField
              source="smsRecipients"
              label="TO"
            />
          </If>
          <If conditionFunc={(data) => data.type === TaskType.EVENT}>
            <AssignmentsField
              source="inviteRecipients"
              label="INVITED"
            />
            <If conditionFunc={(data) => data.location}>
              <TextField source="location" label="Location" />
            </If>
          </If>
        </>
      ) : undefined}
      rightLine1={!props.shortView ? (
        <>
          <DueDateField />
          <If conditionFunc={(data) => data.calculatedDueDate}>
            <div className={styles.calculatedDate}>
              <DateField source="calculatedDueDate" />
            </div>
          </If>
        </>
      ) : null}
      rightLine2={!props.shortView ? (
        <SimpleBlock className={styles.iconsBlock}>
          <IsAiTaskField />
          <TasksGroupField />
          <IsBusinessDay />
          <IsAutoSentField source="isAutoSent" />
          <IsPrivateField />
          <IsKeyDateField source="isKeyDate" />
          <IsRoadmapField source="isIncludedToRoadmap" />
        </SimpleBlock>
      ) : undefined}
      deleteButton={!props.shortView ? <DeletePlanTaskButton /> : undefined}
      isCollapsed={props.shortView}
    />
  );
}
